/**
 * WebSocket wire protocol.
 *
 * This is the SAME contract as the backend `realtime/events.py` (ARCHITECTURE §6).
 * Both the dashboard and the widget consume it via `WsClient`. Keep the event
 * names below byte-for-byte identical to the backend event constants.
 */

import type { ChatSession, Message, SessionParticipant, SessionStatus } from './chat';
import type { AgentDm, AgentPresence } from './agent';
import type { ChannelType } from './channel';

/** Bumped when the envelope shape changes in a backwards-incompatible way. */
export const WS_PROTOCOL_VERSION = 1 as const;

/**
 * Domain event names — MUST match `backend/app/realtime/events.py` exactly.
 * These are the events fanned out over Redis pub/sub to agent + widget sockets.
 */
export type WsEventType =
  | 'message.created'
  | 'session.incoming'
  | 'session.claimed'
  | 'session.assigned'
  | 'session.closed'
  | 'session.rated'
  | 'session.participant_added'
  | 'agent.presence'
  | 'typing'
  | 'cobrowse.control'
  | 'agent.dm';

/** All domain event names, for iteration / validation. */
export const WS_EVENT_TYPES: readonly WsEventType[] = [
  'message.created',
  'session.incoming',
  'session.claimed',
  'session.assigned',
  'session.closed',
  'session.rated',
  'session.participant_added',
  'agent.presence',
  'typing',
  'cobrowse.control',
  'agent.dm',
] as const;

/**
 * Transport-level control frames handled by `WsClient` itself (not domain
 * events). Kept separate from {@link WsEventType} so app code only ever
 * switches over domain events. Not part of the backend `events.py` union.
 */
export type WsControlType = 'hello' | 'ping' | 'pong' | 'ack' | 'resume' | 'error';

/** ---- Domain event payloads (one per {@link WsEventType}) ---- */

export interface MessageCreatedPayload {
  sessionId: string;
  message: Message;
  /** Present when the event also mutates session summary fields. */
  session?: Pick<
    ChatSession,
    'id' | 'status' | 'lastMessageAt' | 'lastMessagePreview' | 'unreadCount'
  >;
}

/**
 * A brand-new unclaimed (incoming/broadcast) session announced on the org bus so agent
 * dashboards insert the row live — carries the FULL session so no follow-up fetch is needed
 * (the fetch fallback used to race the still-open visitor transaction and 404, which is why
 * new incoming chats only appeared after a manual refresh).
 */
export interface SessionIncomingPayload {
  sessionId: string;
  session: ChatSession;
}

export interface SessionClaimedPayload {
  sessionId: string;
  claimedByAgentId: string;
  status: SessionStatus;
  /** Whether the visitor can voice-call this agent (org softphone ready + agent has a number). */
  agentCallable?: boolean;
}

export interface SessionAssignedPayload {
  sessionId: string;
  assignedToAgentId: string;
  assignedByAgentId: string | null;
  status: SessionStatus;
  agentCallable?: boolean;
}

export interface SessionClosedPayload {
  sessionId: string;
  closedByAgentId: string | null;
  status: SessionStatus;
}

/** Visitor submitted a post-chat rating — dashboards patch the session row live. */
export interface SessionRatedPayload {
  sessionId: string;
  /** Overall CSAT 1-5. */
  rating: number;
  /** Separate agent/service rating (dual-rating mode); null when not collected. */
  agentRating: number | null;
  ratingComment: string | null;
}

/** A collaborating agent joined the session — carries the full participant list. */
export interface SessionParticipantAddedPayload {
  sessionId: string;
  /** The agent that was just added. */
  agentId: string;
  role: string;
  participants: SessionParticipant[];
}

export interface AgentPresencePayload {
  agentId: string;
  presence: AgentPresence;
}

export interface TypingPayload {
  sessionId: string;
  /** Who is typing. */
  actor: 'visitor' | 'agent';
  actorId: string | null;
  channel: ChannelType;
  isTyping: boolean;
}

/** Maps each domain event name to its payload type. */
/** Agent started/stopped co-browsing this session (widget begins/ends DOM streaming). */
export interface CobrowseControlPayload {
  sessionId: string;
  action: 'start' | 'stop';
}

/** A staff DM pushed on the two peers' PRIVATE per-agent channels (never the org bus). */
export interface AgentDmPayload {
  message: AgentDm;
}

export interface WsEventPayloadMap {
  'message.created': MessageCreatedPayload;
  'session.incoming': SessionIncomingPayload;
  'cobrowse.control': CobrowseControlPayload;
  'agent.dm': AgentDmPayload;
  'session.claimed': SessionClaimedPayload;
  'session.assigned': SessionAssignedPayload;
  'session.closed': SessionClosedPayload;
  'session.rated': SessionRatedPayload;
  'session.participant_added': SessionParticipantAddedPayload;
  'agent.presence': AgentPresencePayload;
  typing: TypingPayload;
}

/**
 * The versioned envelope every frame is wrapped in.
 *
 * @typeParam T - the event name (a {@link WsEventType} for domain events).
 *
 * `id` is a monotonic, server-assigned event id used by `WsClient` for
 * resume-since-lastEventId; client-originated frames may omit it. `ts` is the
 * server timestamp (ISO-8601). `orgId`/`sessionId` scope the fan-out.
 */
export interface WsEnvelope<T extends WsEventType = WsEventType> {
  v: typeof WS_PROTOCOL_VERSION;
  type: T;
  /** Server-assigned monotonic event id (for resume); absent on client sends. */
  id?: string;
  ts: string;
  orgId?: string;
  sessionId?: string;
  data: WsEventPayloadMap[T];
}

/** A domain envelope of any event type (discriminated by `type`). */
export type AnyWsEnvelope = {
  [T in WsEventType]: WsEnvelope<T>;
}[WsEventType];

/** Transport control frame (heartbeat, resume, ack, hello, error). */
export interface WsControlFrame {
  v: typeof WS_PROTOCOL_VERSION;
  type: WsControlType;
  /** For `resume`: the last event id the client successfully processed. */
  lastEventId?: string;
  ts?: string;
  /** Freeform control payload (error detail, ack target, hello params…). */
  data?: Record<string, unknown>;
}

/** Anything that can travel on the socket: a domain event or a control frame. */
export type WsFrame = AnyWsEnvelope | WsControlFrame;

/** Narrowing helper: is this frame a domain event (vs a control frame)? */
export function isWsEvent(frame: WsFrame): frame is AnyWsEnvelope {
  return (WS_EVENT_TYPES as readonly string[]).includes(frame.type);
}
